import { PaperProps, Paper, Tooltip, Box, Typography, Collapse } from "@mui/material";
import { ReactNode, useState, MouseEvent, useEffect } from "react";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';

interface CollapsibleSectionProps {
  title: string;
  icon?: ReactNode;
  defaultExpanded?: boolean;
  children: ReactNode;
  paperProps?: PaperProps;
  actionButton?: ReactNode;
  /**
   * Optional unique identifier for this section.
   * If provided, the collapsed state will be stored in localStorage.
   * If not provided, a unique ID will be generated based on the title.
   */
  id?: string;
  /**
   * Optional namespace to further differentiate sections with the same title in different contexts
   * For example: 'studio-tab' vs 'preview-page'
   */
  namespace?: string;
  /** Optional summary displayed in header when collapsed */
  collapsedSummary?: ReactNode;
  /** Optional callback when section is expanded */
  onExpand?: () => void;
  /** Optional status validation - shows status icon when enabled */
  status?: {
    enabled: boolean;
    state: 'valid' | 'warning' | 'error';
    /** Optional list of missing/invalid fields to show in tooltip */
    missingFields?: string[];
  };
}

const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({
  title,
  icon,
  defaultExpanded = true,
  children,
  paperProps = {},
  actionButton,
  id,
  namespace,
  collapsedSummary,
  onExpand,
  status,
}) => {
  // Generate a stable ID for localStorage if not provided
  const storageId = `collapsible-section-${namespace ? `${namespace}-` : ''}${id || title.toLowerCase().replace(/\s+/g, '-')}`;
  
  // Initialize state from localStorage if available, otherwise use defaultExpanded
  const [isCollapsed, setIsCollapsed] = useState(() => {
    try {
      const storedValue = localStorage.getItem(storageId);
      return storedValue !== null ? JSON.parse(storedValue) : !defaultExpanded;
    } catch (e) {
      console.warn(`Error reading localStorage for ${storageId}:`, e);
      return !defaultExpanded;
    }
  });

  // Update localStorage whenever isCollapsed changes
  useEffect(() => {
    try {
      localStorage.setItem(storageId, JSON.stringify(isCollapsed));
    } catch (e) {
      console.warn(`Error writing to localStorage for ${storageId}:`, e);
    }
  }, [isCollapsed, storageId]);

  // Call onExpand when section is expanded
  useEffect(() => {
    if (!isCollapsed && onExpand) {
      onExpand();
    }
  }, [isCollapsed, onExpand]);

  const handleToggleCollapse = (event: MouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsCollapsed((prev: any) => !prev);
  };

  // Ensure clicks on the action area don't toggle collapse, and always expand the section
  const handleActionAreaClick = (event: MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsCollapsed(false);
  };

  const getStatusIcon = (state: 'valid' | 'warning' | 'error', missingFields?: string[]) => {
    const hasFieldDetails = missingFields && missingFields.length > 0;
    
    switch (state) {
      case 'valid':
        return (
          <Tooltip title="All fields valid" arrow>
            <CheckCircleIcon sx={{ fontSize: 18, color: 'success.main', ml: 0.5 }} />
          </Tooltip>
        );
      case 'warning':
        return (
          <Tooltip 
            title={
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                  Missing important fields:
                </Typography>
                {hasFieldDetails ? (
                  <Box component="ul" sx={{ m: 0, pl: 2 }}>
                    {missingFields.map((field, index) => (
                      <li key={index}>{field}</li>
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2">
                    Some important fields are missing
                  </Typography>
                )}
              </Box>
            } 
            arrow
          >
            <WarningIcon sx={{ fontSize: 18, color: 'warning.main', ml: 0.5 }} />
          </Tooltip>
        );
      case 'error':
        return (
          <Tooltip 
            title={
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                  Missing required fields:
                </Typography>
                {hasFieldDetails ? (
                  <Box component="ul" sx={{ m: 0, pl: 2 }}>
                    {missingFields.map((field, index) => (
                      <li key={index}>{field}</li>
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2">
                    Some required fields are missing or invalid
                  </Typography>
                )}
              </Box>
            } 
            arrow
          >
            <ErrorIcon sx={{ fontSize: 18, color: 'error.main', ml: 0.5 }} />
          </Tooltip>
        );
    }
  };

  return (
    <Paper 
      elevation={0} 
      variant="outlined" 
      sx={{ p: 2, mb: 3, bgcolor: 'background.default', ...paperProps?.sx }}
      {...paperProps}
    >
      <Tooltip title={isCollapsed ? "Click to expand" : "Click to collapse"}>
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            cursor: 'pointer',
            '&:hover': {
              bgcolor: 'action.hover',
              borderRadius: '4px',
            },
            p: 1,
            userSelect: 'none',
          }}
          onClick={handleToggleCollapse}
          onDoubleClick={handleToggleCollapse}
        >
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
            {icon} {title}
            {status?.enabled && getStatusIcon(status.state, status.missingFields)}
            {isCollapsed && (
              <Typography variant="body2" color="text.secondary" sx={{ ml: 2, fontStyle: 'italic' }}>
                (collapsed)
              </Typography>
            )}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {isCollapsed && collapsedSummary && (
              <Typography variant="body2" color="text.secondary" sx={{ mr: 1, fontStyle: 'italic', maxWidth: 520, textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
                {collapsedSummary}
              </Typography>
            )}
            {/* Ensure clicks on the action button area don't toggle the collapse and instead expand the section */}
            {actionButton && (
              <Box onClick={handleActionAreaClick} onDoubleClick={handleActionAreaClick} sx={{ display: 'flex', alignItems: 'center' }}>
                {actionButton}
              </Box>
            )}
            {isCollapsed ? (
              <ExpandMoreIcon color="action" />
            ) : (
              <ExpandLessIcon color="action" />
            )}
          </Box>
        </Box>
      </Tooltip>

      <Collapse in={!isCollapsed}>
        <Box sx={{ m: '10px' }}>
          {children}
        </Box>
      </Collapse>
    </Paper>
  );
};

export default CollapsibleSection;